// src/components/kiosk/POSSection/POSHeader.jsx
import { Link, useLocation } from 'react-router-dom';

function POSHeader({ user, onLogout }) {
  const location = useLocation();
  const isPOSActive = location.pathname.includes('/pos');
  const isInventoryActive = location.pathname.includes('/inventory');
  const isCommsActive = location.pathname.includes('/comms-marketing');
  const isSignageActive = location.pathname.includes('/signage');
  const isAttendanceActive = location.pathname.includes('/attendance');
  const isPayrollActive = location.pathname.includes('/payroll');

  const isManagerOrCS = ['customer_service', 'manager'].includes(user?.role);
  const isManager = user?.role === 'manager';

  // Handle logout properly
  const handleLogoutClick = () => {
    if (onLogout && typeof onLogout === 'function') {
      onLogout();
    } else {
      // Fallback if onLogout not provided
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  // Dynamic theming based on current page
  const getThemeColors = () => {
    if (isPOSActive) {
      return {
        primary: '#3b82f6', // Blue
        primaryLight: 'rgba(7, 72, 177, 0.8)',
        primaryBorder: 'rgba(59, 130, 246, 0.5)',
        activeBg: 'rgba(59, 130, 246, 0.5)',
        activeBorder: 'rgba(130, 168, 230, 0.6)'
      };
    } else if (isInventoryActive) {
      return {
        primary: '#10b981', // Green
        primaryLight: 'rgba(4, 66, 45, 0.8)',
        primaryBorder: 'rgba(16, 185, 129, 0.5)',
        activeBg: 'rgba(16, 185, 129, 0.5)',
        activeBorder: 'rgba(52, 211, 153, 0.6)'
      };
    } else if (isCommsActive) {
      return {
        primary: '#eab308', // Yellow
        primaryLight: 'rgba(116, 87, 2, 0.8)',
        primaryBorder: 'rgba(60, 66, 4, 0.5)',
        activeBg: 'rgba(234, 179, 8, 0.5)',
        activeBorder: 'rgba(238, 224, 169, 0.6)'
      };
    } else if (isSignageActive) {
      return {
        primary: '#9333ea', // Purple
        primaryLight: 'rgba(147, 51, 234, 0.8)',
        primaryBorder: 'rgba(147, 51, 234, 0.5)',
        activeBg: 'rgba(147, 51, 234, 0.5)',
        activeBorder: 'rgba(167, 139, 250, 0.6)'
      };
    } else if (isAttendanceActive) {
      return {
        primary: '#f59e0b', // Amber
        primaryLight: 'rgba(120, 70, 4, 0.8)',
        primaryBorder: 'rgba(245, 158, 11, 0.5)',
        activeBg: 'rgba(245, 158, 11, 0.5)',
        activeBorder: 'rgba(252, 211, 77, 0.6)'
      };
    } else if (isPayrollActive) {
      return {
        primary: '#0ea5e9', // Sky
        primaryLight: 'rgba(3, 68, 105, 0.8)',
        primaryBorder: 'rgba(14, 165, 233, 0.5)',
        activeBg: 'rgba(14, 165, 233, 0.5)',
        activeBorder: 'rgba(125, 211, 252, 0.6)'
      };
    }
    // Default to blue
    return {
      primary: '#3b82f6',
      primaryLight: 'rgba(59, 130, 246, 0.8)',
      primaryBorder: 'rgba(59, 130, 246, 0.5)',
      activeBg: 'rgba(59, 130, 246, 0.5)',
      activeBorder: 'rgba(130, 168, 230, 0.6)'
    };
  };

  const theme = getThemeColors();

  // Extract ONLY first name before first dot, then capitalize
  const getDisplayName = () => {
    // Priority 1: Use firstName if available
    if (user?.firstName) {
      return user.firstName;
    }
    
    // Priority 2: Extract first name from username
    if (user?.username) {
      let firstName;
      
      // Check if username has dots
      if (user.username.includes('.')) {
        // Take everything BEFORE the first dot
        firstName = user.username.split('.')[0];
      } else {
        // No dots, use full username
        firstName = user.username;
      }
      
      // Capitalize first letter, lowercase rest
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    }
    
    // Priority 3: Fallback
    return 'Staff';
  };
  
  const displayName = getDisplayName();

  const linkStyle = (isActive) => ({
    padding: '0.375rem 0.75rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    fontSize: '12px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    backgroundColor: isActive ? theme.activeBg : 'transparent',
    color: '#ffffff',
    border: isActive ? `1px solid ${theme.activeBorder}` : '1px solid transparent',
    whiteSpace: 'nowrap'
  });

  return (
    <header
      className="flex flex-wrap items-center justify-between gap-2 mb-4 md:mb-6"
      style={{
        padding: '0.75rem',
        borderRadius: '0.75rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        background: theme.primaryLight,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${theme.primaryBorder}`,
        color: '#ffffff',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}
    >
      {/* Nav links */}
      <div className="flex flex-wrap gap-1">
        <Link to="/kiosk/pos/salon" style={linkStyle(isPOSActive)}>POS</Link>
        <Link to="/kiosk/inventory" style={linkStyle(isInventoryActive)}>Inventory</Link>
        {(isManagerOrCS || user?.role === 'admin') && (
          <Link to="/kiosk/comms-marketing" style={linkStyle(isCommsActive)}>Comms</Link>
        )}
        {(isManagerOrCS || user?.role === 'admin') && (
          <Link to="/kiosk/signage" style={linkStyle(isSignageActive)}>Signage</Link>
        )}
        {(isManager || user?.role === 'admin') && (
          <Link to="/kiosk/attendance" style={linkStyle(isAttendanceActive)}>Attendance</Link>
        )}
        {(isManager || user?.role === 'admin') && (
          <Link to="/kiosk/payroll" style={linkStyle(isPayrollActive)}>Payroll</Link>
        )}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          fontSize: '12px',
          color: '#ffffff',
          fontWeight: '500',
          padding: '0.3rem 0.625rem',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '0.5rem',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <span style={{ opacity: 0.8 }}>{user?.role === 'customer_service' ? 'CS:' : user?.role === 'manager' ? 'Manager:' : 'Cashier:'}</span>
          <span style={{ fontWeight: '600' }}>{displayName}</span>
        </div>
        <button
          onClick={handleLogoutClick}
          style={{
            padding: '0.3rem 0.75rem',
            borderRadius: '0.5rem',
            fontWeight: '500',
            fontSize: '12px',
            transition: 'all 0.3s ease',
            backgroundColor: 'rgba(239, 68, 68, 0.3)',
            color: '#ffffff',
            border: '1px solid rgba(239, 68, 68, 0.5)',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ef4444'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.3)'; }}
        >
          Logout
        </button>
      </div>
    </header>
  );
}

export default POSHeader;